import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import TimeService from "src/services/timeService/timeService";
import { useDispatch } from "src/store";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store";
import { Typography } from "@mui/material";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";
import { ColumnDef } from "@tanstack/react-table";

import { Rollout } from "../../types";

import { RolloutProgressCell } from "./RolloutProgressCell";
import { RolloutStatusCell } from "./RolloutStatusCell";
import { RolloutTableActionsCell } from "./RolloutTableActionsCell";
import { RolloutTableCell } from "./RolloutTableCell";
import { RolloutTableDateCell } from "./RolloutTableDateCell";

const useStyles = makeStyles()(() => ({
    installWindowTooltip: {
        whiteSpace: "pre-line",
    },
}));

const RolloutInstallWindowCell = ({ rollout }: { rollout: Rollout }) => {
    const { t } = useTranslation(["sidepanels", "pages"]);
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const { installWindowStart: start, installWindowEnd: end } = rollout;

    const onClick = () => dispatch(showDetails({ type: "rollout", selectedId: rollout.id }));

    if (!start || !end) {
        return (
            <MinimalTableCell onClick={onClick}>
                <Typography>{t("sidepanels:rolloutPanel.confirm.scheduleWhenFound")}</Typography>
            </MinimalTableCell>
        );
    }

    const format = (value: string, as: "date" | "dateTime") => TimeService.getLocalDate(value, "ISO", as);

    return (
        <MinimalTableCell onClick={onClick}>
            <Tooltip
                title={
                    <span className={classes.installWindowTooltip}>
                        {t("pages:rolloutsPage.table.installWindowTooltip", {
                            start: format(start, "dateTime"),
                            end: format(end, "dateTime"),
                        })}
                    </span>
                }
            >
                <Typography>
                    {t("sidepanels:rolloutPanel.confirm.scheduleTimeWindow", {
                        start: format(start, "date"),
                        end: format(end, "date"),
                    })}
                </Typography>
            </Tooltip>
        </MinimalTableCell>
    );
};

export const columns: ColumnDef<Rollout>[] = [
    {
        header: i18n.t("common:label.version"),
        accessorKey: "version",
        cell: (info) => <RolloutTableCell id={info.row.original.id} value={info.getValue<string>()} />,
    },
    {
        header: i18n.t("common:label.created"),
        accessorKey: "created",
        cell: (info) => <RolloutTableDateCell id={info.row.original.id} value={info.getValue<string>()} />,
    },
    {
        header: i18n.t("common:label.status"),
        accessorKey: "status",
        cell: (info) => <RolloutStatusCell id={info.row.original.id} status={info.getValue<Rollout["status"]>()} />,
    },
    {
        header: i18n.t("common:label.progress"),
        accessorKey: "progress",
        cell: (info) => <RolloutProgressCell rollout={info.row.original} />,
    },
    {
        header: i18n.t("common:label.devices"),
        accessorKey: "deviceCount",
        enableSorting: false,
        cell: (info) => <RolloutTableCell id={info.row.original.id} value={String(info.getValue<number>())} />,
    },
    {
        id: "installWindow",
        header: i18n.t("pages:rolloutsPage.table.header.installWindow"),
        enableSorting: false,
        cell: (info) => <RolloutInstallWindowCell rollout={info.row.original} />,
    },
    {
        id: "actions",
        header: "",
        enableSorting: false,
        meta: { align: "right" },
        cell: ({ row }) => <RolloutTableActionsCell rollout={row.original} />,
    },
];
